import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  FC,
} from "react";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  Button,
  Dialog,
  InteractiveContainer,
} from "@jugl-web/ui-components/cross-platform";
import { MobileDrawer } from "@jugl-web/ui-components/mobile";
import { CropImage, CropImageHandle } from "./components/CropImage/CropImage";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as RotateIcon } from "./assets/rotate.svg";
import { ReactComponent as PhotoIcon } from "./assets/photo.svg";

interface CropImageModalProps {
  isOpen: boolean;
  image?: File;
  horizontal?: boolean;
  panelClassName?: string;
  backdropClassName?: string;
  onSave: (image: File) => void;
  onChangeImage: () => void;
  onClose: () => void;
}
export const CropImageModal: FC<CropImageModalProps> = ({
  isOpen,
  image,
  horizontal,
  panelClassName,
  backdropClassName,
  onSave,
  onChangeImage,
  onClose,
}) => {
  const { isMobile } = useAppVariant();
  const [scale, setScale] = useState<number>(100);
  const $cropImage = useRef<CropImageHandle>(null);
  const { t } = useTranslations();

  const handleSave = useCallback(async () => {
    const file = await $cropImage.current?.getImageFile();
    if (file) {
      onSave(file);
    }
  }, [onSave]);

  const handleScale = (e: React.ChangeEvent<HTMLInputElement>) =>
    setScale(Number(e.target.value));

  const handleRotateImage = () => $cropImage.current?.rotate();

  useEffect(() => {
    setScale(100);
  }, [image]);

  const content = useMemo(
    () => (
      <>
        <div className="border-dark-100 flex items-center justify-between border-0 border-b border-solid py-[14px] px-6">
          <span className="text-sm font-semibold text-[#383838]">
            {t({
              id: "avatar-select-component.adjust-image",
              defaultMessage: "Adjust Image",
            })}
          </span>
          <InteractiveContainer onClick={onClose} className="block">
            <CloseIcon />
          </InteractiveContainer>
        </div>
        <div
          className={cx("flex items-center gap-10 py-12 px-14", {
            "flex-col gap-2 p-4": isMobile,
          })}
        >
          <div
            className={cx(
              "flex items-center justify-center",
              horizontal ? "w-[480px]" : "w-[300px]"
            )}
          >
            <CropImage
              image={image}
              scale={scale / 100}
              ref={$cropImage}
              horizontal={horizontal}
            />
          </div>
          <div className="flex w-[300px] flex-col gap-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-grey-700 text-sm font-medium">
                  {t({
                    id: "avatar-select-component.zoom-in-out",
                    defaultMessage: "Zoom in/out",
                  })}
                </span>
                <span className="text-grey-700 font-medium">{scale}%</span>
              </div>
              <input
                type="range"
                min="100"
                max="200"
                step="5"
                value={scale}
                className="jugl__range-slider w-full"
                onChange={handleScale}
              />
            </div>
            <InteractiveContainer
              onClick={handleRotateImage}
              className="bg-grey-200 text-dark hover:bg-grey-300 mt-4 flex items-center gap-2 rounded-lg p-4 font-[Poppins] text-[15px] font-medium transition-colors"
            >
              <RotateIcon />
              {t({
                id: "avatar-select-component.rotate-photo",
                defaultMessage: "Rotate Photo",
              })}
            </InteractiveContainer>
            <InteractiveContainer
              onClick={onChangeImage}
              className="bg-grey-200 text-dark hover:bg-grey-300 flex items-center gap-2 rounded-lg p-4 font-[Poppins] text-[15px] font-medium transition-colors"
            >
              <PhotoIcon />
              {t({
                id: "avatar-select-component.change-photo",
                defaultMessage: "Change Photo",
              })}
            </InteractiveContainer>
            <Button
              fullWidth
              className={cx("mt-8 hidden", horizontal && "block")}
              onClick={handleSave}
            >
              {t({
                id: "common.save",
                defaultMessage: "Save",
              })}
            </Button>
          </div>
        </div>
      </>
    ),
    [t, onClose, isMobile, horizontal, image, scale, onChangeImage, handleSave]
  );

  if (isMobile) {
    return (
      <MobileDrawer
        size="full-screen"
        isOpen={isOpen}
        onClose={onClose}
        className="flex flex-col rounded-2xl"
      >
        <MobileDrawer.Content>
          <div className="flex flex-col">{content}</div>
        </MobileDrawer.Content>
        <MobileDrawer.Actions>
          <Button className="mx-2 w-full max-w-[300px]" onClick={handleSave}>
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </MobileDrawer.Actions>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className={cx("rounded-2xl", panelClassName)}
      backdropClassName={backdropClassName}
    >
      {content}
      <div className={cx("mb-12 flex justify-center", horizontal && "hidden")}>
        <Button className="mx-20 w-full max-w-[300px]" onClick={handleSave}>
          {t({
            id: "common.save",
            defaultMessage: "Save",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
